import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification as apiDelete,
  clearAllNotifications as apiClear,
  type NotificationItem
} from '../api/notifications';

// Compatibilité avec l'interface utilisée par NotificationCenter
export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionLabel?: string;
  actionUrl?: string;
}

const POLL_INTERVAL = 30_000; // 30 secondes

function toNotification(n: NotificationItem): Notification {
  return {
    id: String(n.id),
    type: n.type,
    title: n.title,
    message: n.message,
    timestamp: new Date(n.created_at),
    read: n.read,
    actionLabel: n.action_label,
    actionUrl: n.action_url
  };
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchNotifications();
      setNotifications(data.notifications.map(toNotification));
      setUnreadCount(data.unreadCount);
    } catch {
      // silencieux si pas connecté / backend down
    }
  }, []);

  // Chargement initial + polling toutes les 30s
  useEffect(() => {
    load();
    timerRef.current = setInterval(load, POLL_INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [load]);

  const markAsRead = useCallback(async (id: string) => {
    await markNotificationRead(Number(id));
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(async () => {
    await markAllNotificationsRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  const deleteNotification = useCallback(async (id: string) => {
    await apiDelete(Number(id));
    setNotifications(prev => {
      const next = prev.filter(n => n.id !== id);
      setUnreadCount(next.filter(n => !n.read).length);
      return next;
    });
  }, []);

  const clearAll = useCallback(async () => {
    await apiClear();
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // addNotification gardé pour compatibilité (utilisé nulle part en prod mais évite les erreurs TS)
  const addNotification = useCallback((_n: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    load(); // recharge depuis l'API au lieu d'ajouter localement
  }, [load]);

  return { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification, clearAll, addNotification };
};
